import { Archive, ArrowUpRight, ChevronDown, ChevronRight, Folder, MoreHorizontal, Plus } from 'lucide-react'
import type { TaskBranch } from '../taskTree'
import type { ProjectSummary, RoomMember, WritingTask } from '../types'
import { taskDueState } from '../taskReminders'

type Props = {
  branch: TaskBranch
  depth?: number
  renderChildren?: boolean
  collapsed: Set<string>
  toggle: (id: string) => void
  busy: boolean
  today: string
  members: RoomMember[]
  projects: ProjectSummary[]
  onEdit: (task: WritingTask) => void
  onAdd: (parent: WritingTask) => void
  onChange: (task: WritingTask, body: Partial<WritingTask>) => void
  onOpen: (task: WritingTask) => void
}
export function TaskBranchRow({ branch, depth = 0, renderChildren = true, ...props }: Props) {
  const { task, children, matched, progress } = branch
  const closed = props.collapsed.has(task.id)
  const group = task.kind === 'group'
  const owner = props.members.find(m => m.id === task.assigneeId)?.displayName || 'Unassigned'
  const story = props.projects.find(p => p.id === task.projectId)
  const due = taskDueState(task, props.today)
  return <li className={'task-branch' + (group || !matched ? ' task-branch--context' : '')}>
    <article id={'task-' + task.id} tabIndex={-1} className="task-tree-row"
      style={{ '--task-depth': Math.min(depth, 4) } as React.CSSProperties}>
      <div className="task-tree-main">
        <button type="button" className="task-expand" aria-label={(closed ? 'Expand ' : 'Collapse ') + task.title}
          aria-expanded={!closed} disabled={!children.length} onClick={() => props.toggle(task.id)}>
          {children.length ? closed ? <ChevronRight size={16} /> : <ChevronDown size={16} /> : null}
        </button>
        {matched ? <label className="task-tree-check">
          <input type="checkbox" checked={task.status === 'done'} disabled={props.busy}
            aria-label={(task.status === 'done' ? 'Reopen task ' : 'Complete task ') + task.title}
            onChange={() => props.onChange(task, { status: task.status === 'done' ? 'todo' : 'done' })} />
        </label> : <span className="task-group-icon"><Folder size={17} /></span>}
        <div className="task-tree-copy">
          <button type="button" className={'task-tree-title' + (matched && task.status === 'done' ? ' is-done' : '')}
            title={task.reference && story ? 'Open linked writing' : 'Edit task'}
            onClick={() => task.reference && story ? props.onOpen(task) : props.onEdit(task)}>{task.title}</button>
          <div className="task-tree-meta">
            {depth > 4 && <span>Level {depth + 1}</span>}
            {(group || children.length > 0) && <span className="task-progress" aria-label={`${progress.done} of ${progress.total} tracked tasks complete`}>
              {progress.done}/{progress.total} done
            </span>}
            {!group && <span>{task.scope === 'personal' ? 'Only you' : owner}</span>}
            {!matched && !group && <span>{task.archived ? 'Archived parent' : task.status === 'done' ? 'Completed parent' : 'Parent task'}</span>}
            {task.dueDate && !group && <time className={'task-due task-due--' + due} dateTime={task.dueDate}>
              {due === 'today' ? 'Today' : due === 'overdue' ? 'Overdue · ' + task.dueDate : task.dueDate}
            </time>}
            {task.projectId && <span>{story?.title || 'Story no longer available'}</span>}
          </div>
          {task.details && <details className="task-notes"><summary>Notes</summary><p>{task.details}</p></details>}
        </div>
        <div className="task-row-actions">
          {task.projectId && <button type="button" className="icon-button task-open-writing" disabled={!story || props.busy}
            aria-label={'Open writing for ' + task.title} title="Open linked writing" onClick={() => props.onOpen(task)}><ArrowUpRight size={16} /></button>}
          <button type="button" className="icon-button" disabled={props.busy} aria-label={'Add subtask to ' + task.title}
            title="Add subtask" onClick={() => props.onAdd(task)}><Plus size={16} /></button>
          <details className="task-row-menu">
            <summary aria-label={'Actions for ' + task.title} title="Task actions"><MoreHorizontal size={16} /></summary>
            <div>
              {task.projectId && <button type="button" disabled={!story || props.busy} onClick={e => {
                e.currentTarget.closest('details')!.open = false; props.onOpen(task)
              }}>Open linked writing</button>}
              <button type="button" disabled={props.busy} onClick={e => { e.currentTarget.closest('details')!.open = false; props.onEdit(task) }}>
                {group ? 'Edit group' : 'Edit task'}
              </button>
              {!group && <button type="button" disabled={props.busy} onClick={e => {
                e.currentTarget.closest('details')!.open = false
                props.onChange(task, { archived: !task.archived })
              }}><Archive size={13} /> {task.archived ? 'Restore task' : 'Archive task'}</button>}
            </div>
          </details>
        </div>
      </div>
    </article>
    {renderChildren && !closed && children.length > 0 && <ul className="task-tree-children">
      {children.map(child => <TaskBranchRow key={child.task.id} branch={child} depth={depth + 1} {...props} />)}
    </ul>}
  </li>
}
